import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ProjectDayTimeTracked,
  ProjectTimeEntry,
  ProjectWithStatus,
  TimeEntryProps,
} from '../../../../Projects/types';
import ButtonLink from '../ButtonLink';
import { ButtonsContainer } from '../ButtonsContainer';
import SubmitButton from '../SubmitButton';
import CancelButton from '../CancelButton';
import ProjectDayTimeEntry from './ProjectDayTimeEntry';
import DayTimeInputLabels from './DayTimeInputLabels';
import { notEmpty } from '../../../../../../../utils/array';
import { TimeEntryCardLayout } from '../TimeEntryCardLayout';
import { CostCenter } from '../../../../../__generated__/graphql';
import { WarningModal } from '../../../components/WarningModal/WarningModal';
import { useTimeTrackingRestriction } from '../../../../../hooks/timeTrackingSettings';
import { toISODateOnly } from '../../../../../../../utils/dateUtils';
import { useDefaultRecordTypeName } from '../../../../../components/ExtraFields/useDefaultRecordTypeName';
import { ExtraFieldsSupportedObject } from '../../../../../components/ExtraFields';
import DayTrackedInfo from './DayTrackedInfo';
import { useMyTimeTrackingSettings } from '../../../../../hooks/timeTracking/useMyTimeTrackingSettings';
import { WorkloadTrackingType } from '../../../../../__generated__/graphql';
import { TimeEntryData } from './types';
import {
  createEmptyItem,
  getActiveProjectId,
  mapToFormData,
  mapToProjectTimeEntry,
} from './mapping';

type Props = {
  disabled: boolean;
  disableSubmit: boolean;
  restrictedFutureEntries: boolean;
  projects: ProjectWithStatus[];
  costCenters: ReadonlyArray<CostCenter>;
  timesTracked: ProjectDayTimeTracked[];
  showTimeEntries: boolean;
  timeEntries: TimeEntryProps[];
  onSave: (timesTracked: ProjectTimeEntry[]) => Promise<void>;
  day: Date;
  compactMode: boolean;
  setDayTotalTracked: (dayTotalTracked: number) => void;
  onProjectTimeEntriesMinutesChange?: (minutes: number) => void;
  autoAddFirstEntry?: boolean;
  onCancel?: () => void;
  initialHours?: number;
  showTimeEntryColumn?: boolean;
  i18prefix: string;
  noForm?: boolean;
};

const DayProjectTimeEntriesForm: React.FC<Props> = ({
  disabled,
  disableSubmit,
  restrictedFutureEntries,
  projects,
  costCenters,
  timesTracked,
  showTimeEntries,
  timeEntries,
  onSave,
  day,
  compactMode = false,
  setDayTotalTracked,
  onProjectTimeEntriesMinutesChange,
  autoAddFirstEntry = false,
  onCancel: onCancelProp,
  initialHours,
  showTimeEntryColumn: showTimeEntryColumnProp,
  i18prefix,
  noForm = false,
}) => {
  const { t } = useTranslation();
  const defaultRecordTypeName = useDefaultRecordTypeName(
    ExtraFieldsSupportedObject.ProjectTimeEntry,
  );

  const {
    getProjectTrackingRestrictedReason,
    setShowRestrictedWarningModal,
    showRestrictedWarningModal,
  } = useTimeTrackingRestriction();

  const { data: trackingSettingsData } = useMyTimeTrackingSettings();
  const workloadTrackingType =
    trackingSettingsData?.me.workloads[0]?.flair__Time_Tracking_Type__c;
  const isTimeTrackingWithProjects =
    workloadTrackingType === WorkloadTrackingType.TimeTrackingWithProjects;

  const showTimeEntryColumn =
    showTimeEntryColumnProp !== undefined
      ? showTimeEntryColumnProp
      : isTimeTrackingWithProjects;
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [updated, setUpdated] = useState<boolean>(false);
  const [projectTimeEntries, setProjectTimeEntries] = useState<TimeEntryData[]>(
    [],
  );

  const restrictionReason = getProjectTrackingRestrictedReason(
    toISODateOnly(day),
  );

  const initData = useCallback(() => {
    setProjectTimeEntries(timesTracked.map(mapToFormData));
  }, [timesTracked]);

  useEffect(() => {
    const totalHours = projectTimeEntries
      .map((projectTimeEntry) => projectTimeEntry.hours || 0)
      .reduce((s, c) => s + c, 0);
    setDayTotalTracked(totalHours);

    const totalMinutes = projectTimeEntries
      .map((projectTimeEntry) => {
        if (!projectTimeEntry.hours) return 0;
        return Math.round(projectTimeEntry.hours * 60);
      })
      .reduce((s, c) => s + c, 0);
    onProjectTimeEntriesMinutesChange?.(totalMinutes);
  }, [
    projectTimeEntries,
    setDayTotalTracked,
    onProjectTimeEntriesMinutesChange,
  ]);

  useEffect(() => {
    if (!updated) {
      initData();
    }
  }, [initData, timesTracked, updated]);

  const getAutoConnectTimeEntryId = useCallback((): string | null => {
    if (isTimeTrackingWithProjects && timeEntries.length === 1) {
      return timeEntries[0].id;
    }
    return null;
  }, [isTimeTrackingWithProjects, timeEntries]);

  useEffect(() => {
    if (
      autoAddFirstEntry &&
      timesTracked.length === 0 &&
      projectTimeEntries.length === 0 &&
      !disabled &&
      projects.length > 0
    ) {
      const activeProjectId = getActiveProjectId(projects);
      const autoConnectTimeEntryId = getAutoConnectTimeEntryId();
      setProjectTimeEntries([
        createEmptyItem(
          activeProjectId,
          defaultRecordTypeName,
          initialHours,
          autoConnectTimeEntryId,
        ),
      ]);
      setIsEditMode(true);
    }
  }, [
    autoAddFirstEntry,
    timesTracked.length,
    projectTimeEntries.length,
    disabled,
    projects,
    defaultRecordTypeName,
    initialHours,
    getAutoConnectTimeEntryId,
  ]);

  const performSave = useCallback(() => {
    setUpdated(true);

    if (restrictionReason) {
      setShowRestrictedWarningModal(true);
    } else {
      const filteredTimeEntries = projectTimeEntries
        .map(mapToProjectTimeEntry)
        .filter(notEmpty);
      onSave(filteredTimeEntries).then(() => {
        setIsEditMode(false);
      });
    }
  }, [
    onSave,
    projectTimeEntries,
    restrictionReason,
    setShowRestrictedWarningModal,
  ]);

  const handleSave = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();
      performSave();
    },
    [performSave],
  );

  const handleSaveClick = useCallback(() => {
    performSave();
  }, [performSave]);

  const handleCancel = useCallback(() => {
    initData();
    setIsEditMode(false);
    onCancelProp?.();
  }, [initData, setIsEditMode, onCancelProp]);

  const handleChange = useCallback(
    (timeEntryToChange: TimeEntryData) => {
      let updatedEntry = timeEntryToChange;

      if (
        isTimeTrackingWithProjects &&
        timeEntries.length === 1 &&
        !updatedEntry.timeEntryId &&
        updatedEntry.hours !== null &&
        updatedEntry.hours > 0
      ) {
        updatedEntry = {
          ...updatedEntry,
          timeEntryId: timeEntries[0].id,
        };
      }

      setProjectTimeEntries((projectTimeEntries) =>
        projectTimeEntries.map((projectTimeEntry) =>
          projectTimeEntry.id !== updatedEntry.id
            ? projectTimeEntry
            : updatedEntry,
        ),
      );
      setIsEditMode(true);
    },
    [
      setProjectTimeEntries,
      setIsEditMode,
      isTimeTrackingWithProjects,
      timeEntries,
    ],
  );

  const handleAddTimeEntry = useCallback(() => {
    if (restrictionReason) {
      setShowRestrictedWarningModal(true);
    } else {
      const autoConnectTimeEntryId = getAutoConnectTimeEntryId();
      setProjectTimeEntries((projectTimeEntries) => [
        ...projectTimeEntries,
        createEmptyItem(
          getActiveProjectId(projects),
          defaultRecordTypeName,
          undefined,
          autoConnectTimeEntryId,
        ),
      ]);
      setIsEditMode(true);
    }
  }, [
    restrictionReason,
    setShowRestrictedWarningModal,
    getAutoConnectTimeEntryId,
    projects,
    defaultRecordTypeName,
  ]);

  const handleDeleteTimeEntry = useCallback((id: string) => {
    setProjectTimeEntries((projectTimeEntries) =>
      projectTimeEntries.filter(
        (projectTimeEntry) => projectTimeEntry.id !== id,
      ),
    );
    setIsEditMode(true);
  }, []);

  const showDayTrackedInfo = showTimeEntries && timeEntries.length > 0;

  const formContent = (
    <TimeEntryCardLayout>
      {showDayTrackedInfo && (
        <div className="mb-4 pb-3 border-bottom">
          <DayTrackedInfo timeEntries={timeEntries} i18prefix={i18prefix} />
        </div>
      )}

      {projectTimeEntries.length > 0 && (
        <DayTimeInputLabels
          className="mb-2"
          showCostCenterLabel={costCenters.length > 0}
          showTimeEntryLabel={showTimeEntryColumn}
          i18prefix={i18prefix}
        />
      )}

      {projectTimeEntries.map((projectTimeEntry, index) => (
        <div key={projectTimeEntry.id}>
          <ProjectDayTimeEntry
            day={toISODateOnly(day)}
            timeTracked={projectTimeEntry}
            projects={projects}
            costCenters={costCenters}
            disabled={disabled}
            onDataChange={handleChange}
            onDelete={handleDeleteTimeEntry}
            compactMode={compactMode}
            showTimeEntryColumn={showTimeEntryColumn}
            timeEntries={timeEntries}
            className={index > 0 ? 'border-top pt-4' : undefined}
          />
        </div>
      ))}
      {restrictedFutureEntries && (
        <div>{t(`${i18prefix}.restrictedFutureEntries`)}</div>
      )}
      {!disabled && (
        <ButtonLink
          disabled={disabled}
          title={t(`${i18prefix}.addEntry`)}
          onClick={handleAddTimeEntry}
        />
      )}
      {isEditMode && (
        <ButtonsContainer>
          <CancelButton
            disabled={disabled}
            onClick={handleCancel}
            title={t('timeTrackingNew.dayCard.cancel')}
          />
          <SubmitButton
            disabled={disabled || disableSubmit}
            isProcessing={disabled || disableSubmit}
            title={t('timeTrackingNew.dayCard.saveChanges')}
            onClick={noForm ? handleSaveClick : undefined}
          />
        </ButtonsContainer>
      )}
    </TimeEntryCardLayout>
  );

  return (
    <>
      <WarningModal
        visible={showRestrictedWarningModal}
        restrictionReason={restrictionReason}
        onClose={() => setShowRestrictedWarningModal(false)}
      />
      {noForm ? (
        <div>{formContent}</div>
      ) : (
        <form onSubmit={handleSave}>{formContent}</form>
      )}
    </>
  );
};

export default DayProjectTimeEntriesForm;
